/**
 * GraphQL client for the AI features (Meeting Coach + Meeting Memory).
 *
 * Mirrors the pattern in `recordingsApi.ts` — talks to the same GraphQL
 * endpoint with the same auth token, no Apollo dependency. Each query
 * here is gated by a feature flag on the server (FEATURE_DISABLED is
 * surfaced via a non-null `errorCode` on the thrown Error).
 */
import config from "../config";
import { abortableDelay } from "./abortableDelay";
import { getAuthToken } from "./providers";

// ---- Coach ----------------------------------------------------------------

export interface MeetingActionItem {
  assignee: string | null;
  task: string;
  dueDate: string | null;
}

export interface MeetingParticipantMetrics {
  userId: string | null;
  screenName: string | null;
  speakingSec: number;
  speakingPct: number;
  interruptions: number;
  wasInterrupted: number;
  questionsAsked: number;
  questionsUnanswered: number;
}

export interface MeetingArtifacts {
  recordingId: string;
  threadId: string | null;
  status: "pending" | "done" | "failed";
  error: string | null;
  durationSec: number;
  topics: string[];
  decisions: string[];
  actionItems: MeetingActionItem[];
  teamSummary: string | null;
  participants: MeetingParticipantMetrics[];
  createdAt: string | null;
  updatedAt: string | null;
}

export interface UserTrendPoint {
  meetingDate: string;
  recordingId: string;
  threadId: string | null;
  durationSec: number;
  speakingSec: number;
  speakingPct: number;
  interruptions: number;
  wasInterrupted: number;
  questionsAsked: number;
  questionsUnanswered: number;
}

export interface UserTrendSummary {
  meetingsCount: number;
  avgSpeakingPct: number;
  avgInterruptions: number;
  totalQuestionsAsked: number;
  points: UserTrendPoint[];
}

// ---- Memory ---------------------------------------------------------------

export type MemorySourceKind = "TRANSCRIPT" | "MESSAGE";

export interface MemorySource {
  index: number;
  kind: MemorySourceKind;
  recordingId: string | null;
  messageId: string | null;
  threadId: string | null;
  speakerName: string | null;
  senderId: string | null;
  startSec: number | null;
  similarity: number;
  text: string;
}

export interface MemoryAnswer {
  answer: string;
  sources: MemorySource[];
}

export interface MemoryHits {
  sources: MemorySource[];
}

// ---- HTTP helpers ---------------------------------------------------------

function getGraphqlUrl(): string {
  const apiUrl = config.apiUrl || "/graphql";
  if (apiUrl.startsWith("/")) return window.location.origin + apiUrl;
  return apiUrl;
}

export class AiFeatureDisabledError extends Error {
  code: string;
  constructor(message: string, code: string) {
    super(message);
    this.name = "AiFeatureDisabledError";
    this.code = code;
    // tsconfig target=es6 — built-in `extends Error` loses the prototype on
    // some runtimes, breaking `instanceof`. Restore it explicitly.
    Object.setPrototypeOf(this, AiFeatureDisabledError.prototype);
  }
}

async function graphqlRequest<T>(query: string, variables: Record<string, unknown>): Promise<T> {
  const token = await getAuthToken();
  const resp = await fetch(getGraphqlUrl(), {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: token } : {}),
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`GraphQL HTTP ${resp.status}: ${text.slice(0, 200)}`);
  }
  const body = await resp.json();
  if (body.errors && body.errors.length > 0) {
    const err = body.errors[0];
    const code = err.extensions?.code;
    if (code === "FEATURE_DISABLED" || code === "AI_UNAVAILABLE" || code === "FEATURE_UNAVAILABLE") {
      throw new AiFeatureDisabledError(err.message || "AI feature disabled", code);
    }
    throw new Error(err.message || "GraphQL error");
  }
  return body.data as T;
}

// ---- Coach queries --------------------------------------------------------

const ARTIFACTS_FIELDS = `
  recordingId
  threadId
  status
  error
  durationSec
  topics
  decisions
  actionItems { assignee task dueDate }
  teamSummary
  participants {
    userId screenName speakingSec speakingPct
    interruptions wasInterrupted
    questionsAsked questionsUnanswered
  }
  createdAt
  updatedAt
`;

export async function fetchMeetingArtifacts(recordingId: string): Promise<MeetingArtifacts | null> {
  const data = await graphqlRequest<{ meetingArtifacts: MeetingArtifacts | null }>(
    `query($id: ID!) { meetingArtifacts(recordingId: $id) { ${ARTIFACTS_FIELDS} } }`,
    { id: recordingId },
  );
  return data.meetingArtifacts;
}

export async function reanalyzeRecording(recordingId: string): Promise<MeetingArtifacts> {
  const data = await graphqlRequest<{ analyzeRecording: MeetingArtifacts }>(
    `mutation($id: ID!) { analyzeRecording(recordingId: $id) { ${ARTIFACTS_FIELDS} } }`,
    { id: recordingId },
  );
  return data.analyzeRecording;
}

export type ArtifactsWatchStatus = "done" | "failed" | "timeout" | "disabled";

export interface ArtifactsWatchResult {
  status: ArtifactsWatchStatus;
  artifacts: MeetingArtifacts | null;
}

export interface ArtifactsWatchOptions {
  pollIntervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
}

const DEFAULT_ARTIFACTS_POLL_MS = 5000;
const DEFAULT_ARTIFACTS_TIMEOUT_MS = 20 * 60 * 1000;

/**
 * Polls `meetingArtifacts` until the Meeting Coach job reaches a terminal
 * state. `null` artifact rows (LLM hasn't run yet) and transient GraphQL
 * errors back off and retry. `FEATURE_DISABLED` short-circuits to "disabled"
 * so callers can stay silent when AI is off on the server.
 */
export async function waitForMeetingArtifacts(
  recordingId: string,
  opts: ArtifactsWatchOptions = {},
): Promise<ArtifactsWatchResult> {
  const pollInterval = opts.pollIntervalMs ?? DEFAULT_ARTIFACTS_POLL_MS;
  const timeout = opts.timeoutMs ?? DEFAULT_ARTIFACTS_TIMEOUT_MS;
  const deadline = Date.now() + timeout;
  let consecutiveErrors = 0;

  // eslint-disable-next-line no-constant-condition
  while (true) {
    if (opts.signal?.aborted) return { status: "timeout", artifacts: null };
    if (Date.now() > deadline) return { status: "timeout", artifacts: null };

    try {
      const a = await fetchMeetingArtifacts(recordingId);
      consecutiveErrors = 0;
      if (a) {
        if (a.status === "done") return { status: "done", artifacts: a };
        if (a.status === "failed") return { status: "failed", artifacts: a };
      }
    } catch (e) {
      if (e instanceof AiFeatureDisabledError) {
        return { status: "disabled", artifacts: null };
      }
      consecutiveErrors = Math.min(consecutiveErrors + 1, 5);
    }

    try {
      await abortableDelay(pollInterval * (1 + consecutiveErrors), opts.signal);
    } catch {
      return { status: "timeout", artifacts: null };
    }
  }
}

export async function fetchMyTrends(days = 30): Promise<UserTrendSummary> {
  const data = await graphqlRequest<{ myMeetingTrends: UserTrendSummary }>(
    `query($days: Int) {
      myMeetingTrends(days: $days) {
        meetingsCount
        avgSpeakingPct
        avgInterruptions
        totalQuestionsAsked
        points {
          meetingDate recordingId threadId durationSec
          speakingSec speakingPct
          interruptions wasInterrupted
          questionsAsked questionsUnanswered
        }
      }
    }`,
    { days },
  );
  return data.myMeetingTrends;
}

// ---- Memory queries -------------------------------------------------------

const SOURCE_FIELDS = `
  index kind recordingId messageId threadId
  speakerName senderId startSec similarity text
`;

export async function searchMemory(
  question: string,
  opts: { threadId?: string | null; topK?: number } = {},
): Promise<MemoryHits> {
  const data = await graphqlRequest<{ searchMemory: MemoryHits }>(
    `query($q: String!, $t: ID, $k: Int) {
      searchMemory(question: $q, threadId: $t, topK: $k) {
        sources { ${SOURCE_FIELDS} }
      }
    }`,
    { q: question, t: opts.threadId || null, k: opts.topK || null },
  );
  return data.searchMemory;
}

export async function askMemory(
  question: string,
  opts: { threadId?: string | null; topK?: number } = {},
): Promise<MemoryAnswer> {
  const data = await graphqlRequest<{ askMemory: MemoryAnswer }>(
    `query($q: String!, $t: ID, $k: Int) {
      askMemory(question: $q, threadId: $t, topK: $k) {
        answer
        sources { ${SOURCE_FIELDS} }
      }
    }`,
    { q: question, t: opts.threadId || null, k: opts.topK || null },
  );
  return data.askMemory;
}

// ---- Helpers --------------------------------------------------------------

export function formatClock(sec: number | null | undefined): string {
  const s = Math.max(0, Math.floor(sec || 0));
  const m = Math.floor(s / 60);
  const ss = (s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}

/**
 * Best-effort deep link to the chat where a recording lives. There's no
 * standalone `/recordings/:id` page yet — the closest thing is the chat
 * thread that contains the inline recording bubble.
 *
 * Returns null when we don't know which thread to open (e.g. the
 * recording was uploaded without a threadId, or it's a personal-thread
 * recording whose userSlug we can't resolve here).
 */
export function buildRecordingChatLink(threadId: string | null | undefined): string | null {
  if (!threadId) return null;
  // Group threads use the slug-based route; personal threads use a
  // different one but the recording metadata stores only one id, so we
  // try the group route first. The chat router will redirect to /home if
  // it doesn't match.
  return `/messages/group/${encodeURIComponent(threadId)}`;
}

/**
 * Citation snippet suitable for copying to clipboard (transcript hits
 * include speaker + time, message hits include sender).
 */
export function buildCitation(source: MemorySource): string {
  const parts: string[] = [];
  if (source.kind === "TRANSCRIPT") {
    if (source.speakerName) parts.push(source.speakerName);
    if (source.startSec != null) parts.push(`@ ${formatClock(source.startSec)}`);
  } else if (source.senderId) {
    parts.push(`thread ${source.threadId || "?"}`);
  }
  const head = parts.join(" ");
  return head ? `${head}: ${source.text}` : source.text;
}
